import React, { useEffect, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  FormControlLabel,
  Checkbox,
  Link,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import loginBackground from "../assests/images/loginbgimage.jpg";
import {
  loginAction,
  forgotPasswordAction,
  verifyOtpAction,
  resetPasswordAction,
} from "../ustils/actioncreator";
import { registerAdminDeviceAfterLogin } from "../services/registerDeviceService";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formErrors, setFormErrors] = useState({});

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState("request");
  const [forgotEmail, setForgotEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotErrors, setForgotErrors] = useState({});

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const getErrorText = (error, fallbackText) =>
    error?.response?.data?.message || error?.message || fallbackText;

  const validateLogin = () => {
    const errors = {};
    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(email.trim())) {
      errors.email = "Please enter a valid email";
    }

    if (!password.trim()) {
      errors.password = "Password is required";
    } else if (password.trim().length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForgotRequest = () => {
    const errors = {};
    if (!forgotEmail.trim()) {
      errors.forgotEmail = "Email is required";
    } else if (!emailRegex.test(forgotEmail.trim())) {
      errors.forgotEmail = "Please enter a valid email";
    }
    setForgotErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForgotOtp = () => {
    const errors = {};
    if (!otp.trim()) {
      errors.otp = "OTP is required";
    } else if (!/^\d{4,8}$/.test(otp.trim())) {
      errors.otp = "OTP should be 4 to 8 digits";
    }
    setForgotErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateResetPassword = () => {
    const errors = {};
    if (!newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else if (newPassword.trim().length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }
    setForgotErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    if (!validateLogin()) {
      return;
    }
    setLoading(true);

    try {
      const response = await loginAction({ email, password });
      const isSuccess = response?.success;
      const loginData = response?.data || {};

      if (!isSuccess) {
        setErrorMessage(response?.message || "Login failed. Please try again.");
        return;
      }

      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      localStorage.setItem("authResponse", JSON.stringify(response));
      localStorage.setItem("authUser", JSON.stringify(loginData));
      localStorage.setItem("accessToken", loginData?.access_token || "");
      localStorage.setItem("refreshToken", loginData?.refresh_token || "");
      localStorage.setItem("isLoggedIn", "true");

      await registerAdminDeviceAfterLogin();

      navigate("/dashboard/home");
    } catch (error) {
      setErrorMessage(getErrorText(error, "Login failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const openForgotDialog = () => {
    setForgotOpen(true);
    setForgotStep("request");
    setForgotEmail(email || "");
    setOtp("");
    setNewPassword("");
    setForgotMessage("");
    setForgotErrors({});
  };

  const handleSendForgot = async () => {
    if (!validateForgotRequest()) {
      return;
    }
    setForgotLoading(true);
    setForgotMessage("");
    try {
      await forgotPasswordAction({ email: forgotEmail });
      setForgotStep("verify");
      setForgotMessage("OTP sent to your email.");
    } catch (error) {
      setForgotMessage(getErrorText(error, "Failed to send OTP."));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!validateForgotOtp()) {
      return;
    }
    setForgotLoading(true);
    setForgotMessage("");
    try {
      await verifyOtpAction({ email: forgotEmail, otp });
      setForgotStep("reset");
      setForgotMessage("OTP verified. Set your new password.");
    } catch (error) {
      setForgotMessage(getErrorText(error, "OTP verification failed."));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!validateResetPassword()) {
      return;
    }
    setForgotLoading(true);
    setForgotMessage("");
    try {
      await resetPasswordAction({ email: forgotEmail, password: newPassword });
      setForgotMessage("Password reset successful. Please login.");
      setForgotOpen(false);
    } catch (error) {
      setForgotMessage(getErrorText(error, "Password reset failed."));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        overflow: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 2, sm: 3 },
        px: { xs: 0, sm: 1 },
        backgroundImage: `linear-gradient(135deg, rgba(18, 20, 26, 0.72) 0%, rgba(18, 20, 26, 0.45) 45%, rgba(18, 20, 26, 0.55) 100%), url(${loginBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
      }}
    >
      <Container maxWidth="sm" sx={{ display: "flex", justifyContent: "center", width: "100%", px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: "460px",
            p: { xs: 2.5, sm: 3.5, md: 4 },
            borderRadius: { xs: "14px", sm: "18px" },
            backdropFilter: "blur(12px)",
            background: "rgba(20, 17, 17, 0.78)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            color: "#fff",
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          }}
        >
          <Typography variant="h4" fontWeight={700} mb={3} color="#d2c381" sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}>
            Brokkerspot Admin
          </Typography>

          {errorMessage ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={handleLogin}>
            <Typography fontSize={14} mb={0.5}>
              Email
            </Typography>
            <TextField
              fullWidth
              placeholder="Example@mail.com"
              margin="dense"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (formErrors.email) {
                  setFormErrors((prev) => ({ ...prev, email: "" }));
                }
              }}
              error={Boolean(formErrors.email)}
              helperText={formErrors.email || ""}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#fff",
                  borderRadius: "7px",
                },
              }}
            />

            <Typography fontSize={14} mb={0.5}>
              Password
            </Typography>
            <TextField
              fullWidth
              placeholder="********"
              type={showPassword ? "text" : "password"}
              margin="dense"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (formErrors.password) {
                  setFormErrors((prev) => ({ ...prev, password: "" }));
                }
              }}
              error={Boolean(formErrors.password)}
              helperText={formErrors.password || ""}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#fff",
                  borderRadius: "7px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                    size="small"
                    sx={{ color: "#fff", "&.Mui-checked": { color: "#d2c381" } }}
                  />
                }
                label={<Typography fontSize={14}>Remember me</Typography>}
              />
              <Link
                component="button"
                type="button"
                underline="none"
                onClick={openForgotDialog}
                sx={{ color: "#fff", fontSize: 14 }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.2,
                borderRadius: "7px",
                fontWeight: 700,
                textTransform: "none",
                bgcolor: "#d2c381",
                color: "#fff",
                "&:hover": { bgcolor: "#baa96e" },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Login"}
            </Button>
          </Box>
        </Paper>
      </Container>

      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Forgot Password</DialogTitle>
        <DialogContent>
          {forgotMessage ? (
            <Alert severity={forgotMessage.toLowerCase().includes("failed") ? "error" : "success"} sx={{ mb: 2 }}>
              {forgotMessage}
            </Alert>
          ) : null}

          {forgotStep === "request" ? (
            <TextField
              fullWidth
              label="Email"
              margin="dense"
              value={forgotEmail}
              onChange={(event) => {
                setForgotEmail(event.target.value);
                if (forgotErrors.forgotEmail) {
                  setForgotErrors((prev) => ({ ...prev, forgotEmail: "" }));
                }
              }}
              error={Boolean(forgotErrors.forgotEmail)}
              helperText={forgotErrors.forgotEmail || ""}
            />
          ) : null}

          {forgotStep === "verify" ? (
            <TextField
              fullWidth
              label="OTP"
              margin="dense"
              value={otp}
              onChange={(event) => {
                setOtp(event.target.value);
                if (forgotErrors.otp) {
                  setForgotErrors((prev) => ({ ...prev, otp: "" }));
                }
              }}
              error={Boolean(forgotErrors.otp)}
              helperText={forgotErrors.otp || ""}
            />
          ) : null}

          {forgotStep === "reset" ? (
            <TextField
              fullWidth
              label="New Password"
              type="password"
              margin="dense"
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value);
                if (forgotErrors.newPassword) {
                  setForgotErrors((prev) => ({ ...prev, newPassword: "" }));
                }
              }}
              error={Boolean(forgotErrors.newPassword)}
              helperText={forgotErrors.newPassword || ""}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setForgotOpen(false)}>Cancel</Button>
          {forgotStep === "request" ? (
            <Button onClick={handleSendForgot} variant="contained" disabled={forgotLoading || !forgotEmail}>
              Send OTP
            </Button>
          ) : null}
          {forgotStep === "verify" ? (
            <Button onClick={handleVerifyOtp} variant="contained" disabled={forgotLoading || !otp}>
              Verify OTP
            </Button>
          ) : null}
          {forgotStep === "reset" ? (
            <Button
              onClick={handleResetPassword}
              variant="contained"
              disabled={forgotLoading || !newPassword}
            >
              Reset Password
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
}